import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, X, Check, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { vocabularyApi } from '@/api/vocabulary'
import { WORD_DRAG_TYPE } from '@/components/vocabulary/WordCard'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { useSettingsStore } from '@/store/settingsStore'
import type { Collection } from '@/types/user'

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Invalid hex color').optional().or(z.literal('')),
  icon: z.string().max(50).optional(),
})

type FormValues = z.infer<typeof schema>

interface CollectionsPanelProps {
  selectedId?: string
  onSelect: (id: string | undefined) => void
}

export function CollectionsPanel({ selectedId, onSelect }: CollectionsPanelProps) {
  const queryClient = useQueryClient()
  const nativeLanguage = useSettingsStore((s) => s.settings?.nativeLanguage ?? 'en')
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [dropTargetId, setDropTargetId] = useState<string | null>(null)

  const { data: collections = [] } = useQuery({
    queryKey: ['collections', nativeLanguage],
    queryFn: () => vocabularyApi.getCollections().then((r) => r.data),
  })

  const addToCollectionMutation = useMutation({
    mutationFn: ({ collectionId, userWordId }: { collectionId: string; userWordId: string }) =>
      vocabularyApi.addWordToCollection(collectionId, userWordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: FormValues) =>
      vocabularyApi.createCollection({
        ...data,
        color: data.color || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      setShowCreate(false)
      reset()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormValues }) =>
      vocabularyApi.updateCollection(id, { ...data, color: data.color || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vocabularyApi.deleteCollection(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      if (selectedId === id) onSelect(undefined)
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const startEdit = (col: Collection) => {
    setEditingId(col.id)
    setValue('name', col.name)
    setValue('description', col.description ?? '')
    setValue('color', col.color ?? '')
    setValue('icon', col.icon ?? '')
  }

  const handleDropOnCollection = (collectionId: string, e: React.DragEvent) => {
    e.preventDefault()
    setDropTargetId(null)
    const userWordId = e.dataTransfer.getData(WORD_DRAG_TYPE)
    if (!userWordId) return
    addToCollectionMutation.mutate({ collectionId, userWordId })
  }

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Collections</h3>
        <button
          onClick={() => { setShowCreate(true); setEditingId(null) }}
          className="rounded p-1 text-muted-foreground hover:text-foreground"
          aria-label="New collection"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <button
        className={`mb-1 flex w-full items-center rounded-md px-2 py-1.5 text-sm transition-colors ${
          !selectedId ? 'bg-accent font-medium' : 'hover:bg-accent/50'
        }`}
        onClick={() => onSelect(undefined)}
      >
        {copy.vocabulary.allItems}
      </button>

      {collections.map((col) => (
        <div key={col.id} className="group flex items-center gap-1">
          {editingId === col.id ? (
            <form
              className="flex-1"
              onSubmit={handleSubmit((d) => updateMutation.mutate({ id: col.id, data: d }))}
            >
              <div className="space-y-2 pb-2 pt-1">
                <Input placeholder="Name" hasError={!!errors.name} {...register('name')} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                <Input placeholder="Description (optional)" {...register('description')} />
                <div className="flex gap-2">
                  <Input placeholder="#RRGGBB" {...register('color')} />
                  <Input placeholder="Icon" {...register('icon')} />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="h-7 flex-1 text-xs" loading={updateMutation.isPending}>
                    <Check className="h-3 w-3" /> Save
                  </Button>
                  <Button
                    type="button"
                    className="h-7 bg-secondary text-secondary-foreground hover:bg-secondary/80"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <button
                className={cn(
                  'flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                  selectedId === col.id ? 'bg-accent font-medium' : 'hover:bg-accent/50',
                  dropTargetId === col.id && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
                )}
                onClick={() => onSelect(selectedId === col.id ? undefined : col.id)}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes(WORD_DRAG_TYPE)) {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'copy'
                    setDropTargetId(col.id)
                  }
                }}
                onDragLeave={() => setDropTargetId((id) => (id === col.id ? null : id))}
                onDrop={(e) => handleDropOnCollection(col.id, e)}
              >
                {col.icon && <span>{col.icon}</span>}
                {col.color && (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: col.color }}
                  />
                )}
                <span className="truncate">{col.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">{col.wordCount}</span>
              </button>
              <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={() => startEdit(col)}
                  className="rounded p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Edit"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(col.id)}
                  className="rounded p-1 text-muted-foreground hover:text-destructive"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {showCreate && (
        <form
          className="mt-2 space-y-2 border-t pt-2"
          onSubmit={handleSubmit((d) => createMutation.mutate(d))}
        >
          <Label className="text-xs text-muted-foreground">New collection</Label>
          <Input placeholder="Name *" hasError={!!errors.name} {...register('name')} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          <Input placeholder="Description (optional)" {...register('description')} />
          <div className="flex gap-2">
            <Input placeholder="#RRGGBB" {...register('color')} />
            <Input placeholder="Icon (emoji)" {...register('icon')} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="h-7 flex-1 text-xs" loading={createMutation.isPending}>
              Create
            </Button>
            <Button
              type="button"
              className="h-7 bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => { setShowCreate(false); reset() }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </form>
      )}

      <p className="mt-4 hidden items-start gap-2 rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground lg:flex">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        Drag items from the list onto a collection — they stay in your vocabulary.
      </p>
      <p className="mt-4 flex items-start gap-2 rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground lg:hidden">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        Tap the folder icon on a word to add it to a collection.
      </p>
    </div>
  )
}
